import React, { useCallback, useEffect, useRef, useState } from "react";
import cx from "classnames";

import styles from "./TrustpilotBlock.module.scss";

/**
 * Width of the inline layout's rendered content, measured from the widget.
 * Trustpilot doesn't reflow this smaller — below roughly 400px of space it
 * swaps to a different layout entirely — so fitting it into a narrow card
 * means scaling it rather than letting it shrink.
 */
const INLINE_CONTENT_WIDTH = 386;

/**
 * The Trustpilot rating widget, as it sat on the old confirmation page. The
 * bootstrap script is loaded in `public/index.html`; `loadFromElement` is what
 * turns the placeholder div into the rendered widget, and the inner anchor is
 * the fallback if the script is blocked.
 *
 * The starter-pack photo the old standalone block carried above this is gone —
 * this now sits inside the thank-you card, which is a text card.
 */
const TrustpilotBlock = ({ className }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  const onRender = useCallback((node) => {
    if (node && window.Trustpilot) {
      window.Trustpilot.loadFromElement(node, true);
    }
  }, []);

  /**
   * The card is narrower than the widget on a phone — and also between 768px
   * and ~900px, where the two-column grid drops the card to ~339px. Measuring
   * the card rather than the viewport covers both without a breakpoint.
   */
  useEffect(() => {
    const node = containerRef.current;

    if (!node || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;

      // Never scale up — at full size the widget is already correct.
      setScale(width > 0 ? Math.min(1, width / INLINE_CONTENT_WIDTH) : 1);
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={cx(styles.container, className)}
      ref={containerRef}
      style={{ "--tp-scale": scale }}
    >
      <div
        // `trustpilot-widget` is the hook the bootstrap script looks for.
        className={cx("trustpilot-widget", styles.widget)}
        data-locale="en-GB"
        data-template-id="5419b6ffb0d04a076446a9af"
        data-businessunit-id="5919e89d0000ff0005a28068"
        data-style-height="24px"
        data-style-width="100%"
        data-theme="light"
        data-text-color="#959291"
        ref={onRender}
      >
        <a
          href="https://uk.trustpilot.com/review/www.grind.co.uk"
          target="_blank"
          rel="noopener noreferrer"
        >
          Trustpilot
        </a>
      </div>
    </div>
  );
};

export default TrustpilotBlock;
